import { RECIPE_TYPE_LIST } from "@/lib/recipeType";
import type { RecipeType } from "@/lib/types";

export enum RecipeSort {
  NEWEST = "newest",
  OLDEST = "oldest",
}

export type RecipeTypeFilter = "all" | RecipeType;

export type CalorieRange = {
  caloriesMin: number | null;
  caloriesMax: number | null;
};

export type RecipeSearchFilters = CalorieRange & {
  query: string;
  type: RecipeTypeFilter;
  sort: RecipeSort;
};

export const RECIPE_TYPE_FILTER_LIST = [
  "all",
  ...RECIPE_TYPE_LIST,
] satisfies RecipeTypeFilter[];

export const CALORIE_FILTER_OPTIONS = [
  {
    value: "low-calorie",
    label: "Under 100 kcal",
    description: "Low calorie",
    caloriesMin: null,
    caloriesMax: 100,
  },
  {
    value: "light",
    label: "100-200 kcal",
    description: "Light",
    caloriesMin: 100,
    caloriesMax: 200,
  },
  {
    value: "premium",
    label: "Over 200 kcal",
    description: "Premium",
    caloriesMin: 200,
    caloriesMax: null,
  },
] as const;

export function hasCaloriesFilter({ caloriesMin, caloriesMax }: CalorieRange) {
  return caloriesMin !== null || caloriesMax !== null;
}

export function formatCaloriesFilterLabel({
  caloriesMin,
  caloriesMax,
}: CalorieRange) {
  if (caloriesMin !== null && caloriesMax !== null) {
    return `${caloriesMin}-${caloriesMax} kcal/100g`;
  }

  if (caloriesMin !== null) {
    return `>= ${caloriesMin} kcal/100g`;
  }

  if (caloriesMax !== null) {
    return `<= ${caloriesMax} kcal/100g`;
  }

  return "";
}

export function getCaloriesFilterValue({
  caloriesMin,
  caloriesMax,
}: CalorieRange) {
  return (
    CALORIE_FILTER_OPTIONS.find(
      (option) =>
        option.caloriesMin === caloriesMin &&
        option.caloriesMax === caloriesMax,
    )?.value ??
    (caloriesMin === null && caloriesMax === null ? "any" : "custom")
  );
}

export function filterAndSortRecipes<
  T extends {
    name: string;
    type: RecipeType;
    createdAt: string;
  },
>(
  recipes: readonly T[],
  filters: RecipeSearchFilters,
  getCaloriesPer100g: (recipe: T) => number,
) {
  const normalizedQuery = filters.query.trim().toLocaleLowerCase();
  const filteredRecipes: T[] = [];

  for (const recipe of recipes) {
    if (
      normalizedQuery &&
      !recipe.name.toLocaleLowerCase().includes(normalizedQuery)
    ) {
      continue;
    }

    if (filters.type !== "all" && recipe.type !== filters.type) {
      continue;
    }

    if (hasCaloriesFilter(filters)) {
      const caloriesPer100g = getCaloriesPer100g(recipe);

      if (
        (filters.caloriesMin !== null &&
          caloriesPer100g < filters.caloriesMin) ||
        (filters.caloriesMax !== null && caloriesPer100g > filters.caloriesMax)
      ) {
        continue;
      }
    }

    filteredRecipes.push(recipe);
  }

  return filteredRecipes.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return filters.sort === RecipeSort.OLDEST ? dateA - dateB : dateB - dateA;
  });
}
